import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import Landing from "./pages/Landing";
import Calculator from "./pages/Calculator";
// Compare is not imported: its route is retired (see below). The page component is kept in
// pages/Compare.jsx so restoring it is an import and a route, not a rewrite.
import Apply from "./pages/Apply";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Onboarding from "./pages/Onboarding";
import * as React from 'react';
import QuickStart from './pages/QuickStart';
import { useCaptureAttribution } from './lib/formSession';
import { usePageViews } from './lib/analytics';
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import AdminDashboard from "./pages/AdminDashboard";
import DealerDashboard from "./pages/DealerDashboard";
import AnalyticsDashboard from "./pages/AnalyticsDashboard";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import Disclaimer from "./pages/Disclaimer";
import PreApproval from "./pages/PreApproval";
import VerticalLanding from "./pages/VerticalLanding";
import ProtectedRoute from "./components/ProtectedRoute";
import "./App.css";

function AppRouter() {
  const location = useLocation();

  // Capture `?dealer=` wherever it appears — the landing page, /calculator, /apply, a shared deep
  // link. Here rather than in `App`, which renders the router and so sits outside it. Doing it once
  // at the top means no individual page or CTA has to remember, which is what kept going wrong:
  // one entry point that forgets is one dealer who doesn't get paid.
  useCaptureAttribution(React, location.key);

  // GA4 does not see a route change — a SPA never loads a second page. Without this, every visit
  // would be credited to whichever URL was opened first and the six landing pages would show no
  // traffic at all. Keyed on pathname, not `location.key`: a hash change or a re-render is not a
  // new page, and counting it as one would inflate views against every real one.
  usePageViews(React, location.pathname);
  
  // REMINDER: Handle session_id synchronously during render to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      {/* One component, six front doors — see config/verticals.js. An unknown slug redirects home
          rather than 404ing into an empty shell. */}
      <Route path="/finance/:slug" element={<VerticalLanding />} />
      <Route path="/calculator" element={<Calculator />} />
      {/* /compare RETIRED 2026-08-26 (owner: "we don't use all those lenders so no need to mention
          them"). The page's entire content was a grid of NAMED lenders with their rates, fees and
          a "Calculate with {lender}" CTA, all read from a seeded table Levr has no confirmed
          accreditation behind — the same data `Calculator.jsx` had stripped for the same reason.
          Strip the names and nothing is left to compare, so the route is unmounted rather than
          gutted. Nothing linked to it: it was already reachable only by typing the URL.
          `pages/Compare.jsx` is kept intact — restoring this is one line, once a real panel exists.
      <Route path="/compare" element={<Compare />} /> */}
      {/* Redirected, not simply unmounted: with no match React Router renders nothing, so an old
          link, a bookmark or a cached crawl target would land on a blank page that still returns
          200. Sending them to the calculator gives them the one tool the page was next to. */}
      <Route path="/compare" element={<Navigate to="/calculator" replace />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/get-started" element={<Onboarding />} />
      {/* The hero calculator continues here: the same screen as /get-started, three steps. */}
      <Route path="/quick-start" element={<QuickStart />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/disclaimer" element={<Disclaimer />} />
      <Route path="/pre-approval" element={<PreApproval />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route 
        path="/apply" 
        element={
          <ProtectedRoute>
            <Apply />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dealer" 
        element={
          <ProtectedRoute>
            <DealerDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/analytics" 
        element={
          <ProtectedRoute requireAdmin>
            <AnalyticsDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute requireAdmin>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-stone-50">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
